/**
 * BulkPaymentUploadModal — Sep 2026 compact premium modal (replaces the
 * /admin/billing/bulk-payments page). UI/UX only: the three-step workflow
 * (upload → preview/matching → apply result) and every API call are unchanged
 * from pages/BulkPaymentUpload.jsx.
 *
 * Calls:
 *   POST  /api/pricing/billing/payments/bulk-upload         (multipart)
 *   GET   /api/pricing/billing/payments/bulk-upload/{job}   (re-fetch preview)
 *   POST  /api/pricing/billing/payments/bulk-upload/{job}/apply
 *   POST  /api/pricing/billing/payments/bulk-upload/{job}/rows/{row}/resolve
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, CloudUpload, FolderOpen, FileText, CheckCircle2,
  X, Loader2, Sparkles,
} from 'lucide-react';
import { Button } from '../../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  matched:          { label: 'Matched',                color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  partial_payment:  { label: 'Partial Payment',        color: 'bg-blue-100 text-blue-800 border-blue-200' },
  amount_mismatch:  { label: 'Amount Mismatch',        color: 'bg-amber-100 text-amber-800 border-amber-200' },
  duplicate:        { label: 'Duplicate / Already Paid', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  needs_review:     { label: 'Needs Review',           color: 'bg-orange-100 text-orange-800 border-orange-200' },
  unmatched:        { label: 'Unmatched',              color: 'bg-rose-100 text-rose-800 border-rose-200' },
  applied:          { label: 'Applied',                color: 'bg-emerald-200 text-emerald-900 border-emerald-300' },
  apply_failed:     { label: 'Apply Failed',           color: 'bg-rose-200 text-rose-900 border-rose-300' },
  resolved_ignored: { label: 'Ignored',                color: 'bg-slate-100 text-slate-500 border-slate-200' },
  resolved_manual:  { label: 'Resolved (manual)',      color: 'bg-blue-100 text-blue-800 border-blue-200' },
  // BULK-PAY-PID-001 — Tier 0 (Product ID | Fee | Product | State) statuses
  fee_mismatch:         { label: 'Fee Mismatch',         color: 'bg-amber-100 text-amber-800 border-amber-200' },
  product_mismatch:     { label: 'Product Mismatch',     color: 'bg-orange-100 text-orange-800 border-orange-200' },
  state_mismatch:       { label: 'State Mismatch',       color: 'bg-orange-100 text-orange-800 border-orange-200' },
  product_id_not_found: { label: 'Product ID Not Found', color: 'bg-rose-100 text-rose-800 border-rose-200' },
  already_paid:         { label: 'Already Paid',         color: 'bg-slate-100 text-slate-700 border-slate-200' },
};
const EXCEPTION_STATUSES = ['unmatched', 'amount_mismatch', 'needs_review', 'duplicate',
  'fee_mismatch', 'product_mismatch', 'state_mismatch', 'product_id_not_found', 'already_paid'];
const REVIEWABLE_STATUSES = ['fee_mismatch', 'needs_review', 'product_mismatch', 'state_mismatch', 'already_paid'];
const TIER0 = 'product_id_fee';

const fmtUsd = (n) => (n === null || n === undefined || Number.isNaN(Number(n)) ? '—' : `$${Number(n).toFixed(2)}`);
const fmtSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, color: 'bg-slate-100 text-slate-700 border-slate-200' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium border rounded ${meta.color}`} data-testid={`status-pill-${status}`}>
      {meta.label}
    </span>
  );
};

const SummaryCard = ({ label, count, tone = 'slate', delay = 0 }) => {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    blue: 'bg-blue-50 text-blue-900 border-blue-200',
    amber: 'bg-amber-50 text-amber-900 border-amber-200',
    rose: 'bg-rose-50 text-rose-900 border-rose-200',
    slate: 'bg-slate-50 text-slate-800 border-slate-200',
  };
  return (
    <div className={`bpu-stagger rounded-lg border px-3 py-2 ${tones[tone]}`} style={{ animationDelay: `${delay}ms` }}
      data-testid={`summary-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-xl font-semibold mt-0.5 leading-none">{count}</div>
    </div>
  );
};

const downloadSampleCsv = () => {
  // BULK-PAY-TEMPLATE-001 — blank template: required headers + one empty row, no example values.
  const headers = ['Product ID', 'Fee', 'Product', 'State', 'Payment Date', 'Reference Number'];
  const csv = `${headers.join(',')}\n${headers.map(() => '').join(',')}\n`;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url; a.download = 'bulk_payment_template.csv';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ---- Stage 1: upload ----
const UploadStage = ({ file, rowCount, onPick, onClear, dragging, setDragging }) => {
  const fileRef = useRef(null);
  return (
    <div className="px-6 pb-2 space-y-3 bpu-stage">
      <div
        className={`bpu-dropzone relative rounded-xl border-2 border-dashed px-6 py-6 text-center cursor-pointer select-none ${dragging ? 'is-dragging' : ''} ${file ? 'has-file' : ''}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
        onDrop={(e) => { e.preventDefault(); setDragging(false); onPick(e.dataTransfer.files?.[0]); }}
        data-testid="bulk-payment-dropzone"
      >
        <div className="bpu-cloud mx-auto w-11 h-11 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-[0_8px_20px_rgba(79,70,229,0.35)]">
          <CloudUpload className="w-5 h-5" />
        </div>
        <div className="mt-3 text-[15px] font-semibold text-slate-900">{dragging ? 'Release to add your CSV' : 'Drop your CSV file here'}</div>
        <div className="text-xs text-slate-500 mt-0.5">or click to browse</div>
        <Button type="button" variant="ghost" size="sm"
          className="bpu-chip mt-3 h-9 px-4 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800 font-medium"
          onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
          data-testid="bulk-payment-choose-file">
          <FolderOpen className="w-4 h-4 mr-2" /> Choose File
        </Button>
        <div className="mt-2">
          <button type="button" onClick={(e) => { e.stopPropagation(); downloadSampleCsv(); }}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-indigo-700 hover:text-indigo-900 hover:underline underline-offset-2"
            data-testid="bulk-payment-download-sample">
            <FileText className="w-4 h-4" /> Download Sample CSV
          </button>
        </div>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
          onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ''; }}
          data-testid="bulk-payment-file-input" />
      </div>

      {file && (
        <div className="bpu-filecard flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-2.5" data-testid="bulk-payment-file-card">
          <span className="bpu-check w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-900 truncate" data-testid="bulk-payment-file-name">{file.name}</div>
            <div className="text-xs text-slate-500">{fmtSize(file.size)}{rowCount !== null && <> &middot; {rowCount} row{rowCount === 1 ? '' : 's'}</>}</div>
          </div>
          <button type="button" onClick={onClear} aria-label="Remove file"
            className="bpu-icon-btn w-8 h-8 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center"
            data-testid="bulk-payment-file-remove">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// ---- Stage 2 (Tier 0): Product ID | Product | State | Uploaded Fee | Expected Fee | Difference | Match Result ----
const Tier0Table = ({ rows, busy, onIgnore, onReview }) => (
  <div className="overflow-auto max-h-[42vh]">
    <table className="w-full text-sm">
      <thead className="tf-thead text-[11px] sticky top-0 bg-white z-10">
        <tr>
          <th className="text-left px-3 py-2 font-medium">#</th>
          <th className="text-left px-3 py-2 font-medium">Product ID</th>
          <th className="text-left px-3 py-2 font-medium">Product</th>
          <th className="text-left px-3 py-2 font-medium">State</th>
          <th className="text-right px-3 py-2 font-medium">Uploaded Fee</th>
          <th className="text-right px-3 py-2 font-medium">Expected Fee</th>
          <th className="text-right px-3 py-2 font-medium">Difference</th>
          <th className="text-left px-3 py-2 font-medium">Match Result</th>
          <th className="text-left px-3 py-2 font-medium">Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const m = r.match_meta || {};
          const diff = m.fee_difference;
          return (
            <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/40 bpu-row" data-testid={`bulk-payment-row-${r.id}`}>
              <td className="px-3 py-1.5 text-slate-500 text-xs">{r.row_number}</td>
              <td className="px-3 py-1.5 font-mono text-xs" data-testid={`t0-pid-${r.id}`}>{r.product_id || '—'}</td>
              <td className="px-3 py-1.5 text-xs">{r.product_type || '—'}{m.client_product && m.client_product.toLowerCase() !== (r.product_type || '').toLowerCase() && <span className="text-slate-400"> → {m.client_product}</span>}</td>
              <td className="px-3 py-1.5 text-xs">{r.state || '—'}</td>
              <td className="px-3 py-1.5 text-right font-medium">{fmtUsd(r.fee ?? r.amount_paid)}</td>
              <td className="px-3 py-1.5 text-right text-slate-600">{fmtUsd(m.expected_fee)}</td>
              <td className={`px-3 py-1.5 text-right ${diff ? (diff < 0 ? 'text-amber-700' : 'text-rose-700') : 'text-slate-500'}`} data-testid={`t0-diff-${r.id}`}>
                {diff === undefined || diff === null ? '—' : (diff === 0 ? '$0.00' : `${diff > 0 ? '+' : '−'}$${Math.abs(diff).toFixed(2)}`)}
              </td>
              <td className="px-3 py-1.5">
                <div className="flex flex-col gap-0.5">
                  <StatusPill status={r.status} />
                  {m.fee_override && <span className="text-[10px] text-blue-700">fee override approved</span>}
                  {r.reason && r.status !== 'matched' && <span className="text-[10px] text-slate-500 max-w-[220px] truncate" title={r.reason}>{r.reason}</span>}
                </div>
              </td>
              <td className="px-3 py-1.5">
                <div className="flex items-center gap-1">
                  {REVIEWABLE_STATUSES.includes(r.status) && (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => onReview(r)}
                      className="h-7 px-2 text-xs text-indigo-700 hover:bg-indigo-50" data-testid={`bulk-payment-review-${r.id}`}>
                      Review
                    </Button>
                  )}
                  {EXCEPTION_STATUSES.includes(r.status) && (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => onIgnore(r)}
                      className="h-7 px-2 text-xs text-slate-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`bulk-payment-ignore-${r.id}`}>
                      Ignore
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ---- Fee Mismatch / Needs Review dialog: nothing is posted here ----
const ReviewDialog = ({ row, busy, onClose, onIgnore, onAcceptFee }) => {
  const [reason, setReason] = useState('');
  if (!row) return null;
  const m = row.match_meta || {};
  const isFee = row.status === 'fee_mismatch';
  const uploaded = Number(row.fee ?? row.amount_paid ?? 0);
  const lineBalance = m.line_balance ?? m.expected_fee;
  const overLine = isFee && lineBalance !== undefined && lineBalance !== null && uploaded > Number(lineBalance) + 0.01;
  const canAccept = isFee && !overLine && reason.trim().length >= 5 && !busy;
  const Field = ({ k, v, mono }) => (
    <div className="flex justify-between gap-3 py-1 border-b border-slate-100 last:border-0"><span className="text-slate-500">{k}</span><span className={`font-medium text-slate-900 text-right ${mono ? 'font-mono' : ''}`}>{v ?? '—'}</span></div>
  );
  return (
    <DialogPrimitive.Root open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="bpu-overlay fixed inset-0 z-[60]" />
        <DialogPrimitive.Content onInteractOutside={(e) => e.preventDefault()}
          className="bpu-content fixed left-1/2 top-1/2 z-[60] w-[calc(100vw-2rem)] max-w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-[0_24px_64px_rgba(15,23,42,0.28)] focus:outline-none"
          data-testid="bulk-payment-review-dialog">
          <div className="px-6 pt-5 pb-3 flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-base font-semibold text-slate-900">Review row #{row.row_number} — <StatusPill status={row.status} /></DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-xs text-slate-500 mt-1">{row.reason}</DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild><button type="button" aria-label="Close" className="bpu-icon-btn w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center justify-center" data-testid="bulk-payment-review-close"><X className="w-4 h-4" /></button></DialogPrimitive.Close>
          </div>
          <div className="px-6 pb-2 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="font-semibold text-slate-900 mb-1">Uploaded row</div>
              <Field k="Product ID" v={row.product_id} mono /><Field k="Product" v={row.product_type} /><Field k="State" v={row.state} />
              <Field k="Uploaded Fee" v={fmtUsd(row.fee ?? row.amount_paid)} /><Field k="Reference" v={row.payment_reference} />
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="font-semibold text-slate-900 mb-1">Matched order / invoice</div>
              {m.order_number ? (<>
                <Field k="Order" v={m.order_number} mono /><Field k="Product" v={m.client_product} /><Field k="State" v={m.property_state} />
                <Field k="Invoice" v={m.invoice_number} mono /><Field k="Invoice balance" v={fmtUsd(m.invoice_balance)} />
                <Field k="Expected Fee" v={fmtUsd(m.expected_fee)} />
                {isFee && m.line_balance !== undefined && <Field k="Outstanding line balance" v={fmtUsd(m.line_balance)} />}
                {isFee && <Field k="Difference" v={`${uploaded - Number(lineBalance ?? 0) > 0 ? '+' : '−'}$${Math.abs(uploaded - Number(lineBalance ?? 0)).toFixed(2)}`} />}
              </>) : (
                <div className="space-y-1" data-testid="bulk-payment-review-candidates">
                  {(row.candidates || []).length ? row.candidates.map((c) => (
                    <div key={c.order_id} className="rounded border border-slate-100 px-2 py-1">
                      <span className="font-mono">{c.order_number}</span> · {c.client_product} · {c.property_state || '—'} · {c.billing_status || c.status}{c.expected_fee !== undefined && <> · exp. {fmtUsd(c.expected_fee)}</>}
                    </div>
                  )) : <div className="text-slate-500">No order details available.</div>}
                </div>
              )}
            </div>
          </div>
          {isFee && overLine && (
            <div className="mx-6 mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="bulk-payment-review-overpay">
              <strong>Line over-payment — cannot be accepted.</strong> Uploaded {fmtUsd(uploaded)} · Outstanding line balance {fmtUsd(lineBalance)} · Difference +${(uploaded - Number(lineBalance)).toFixed(2)}.
              The excess is never moved to another order or line. Ignore this row or correct the fee in the source file and re-upload.
            </div>
          )}
          {isFee && !overLine && (
            <div className="px-6 pt-2">
              <label className="text-xs font-medium text-slate-700" htmlFor="bpu-reason">Reason for accepting the uploaded fee (required, audited)</label>
              <textarea id="bpu-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                placeholder="e.g. Vendor short-paid per remittance; balance to be pursued separately" data-testid="bulk-payment-review-reason" />
              <p className="text-[11px] text-slate-500 mt-1">Accepting records a payment of <strong>{fmtUsd(row.fee ?? row.amount_paid)}</strong> against invoice {m.invoice_number} when you click Apply. The invoice balance drops by that amount only; the order stays partially paid until settled. Nothing is posted now.</p>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 px-6 py-4">
            <Button variant="outline" disabled={busy} onClick={onClose} className="h-9 px-4 rounded-lg" data-testid="bulk-payment-review-cancel">Close</Button>
            <Button variant="outline" disabled={busy} onClick={() => onIgnore(row)} className="h-9 px-4 rounded-lg text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="bulk-payment-review-ignore">Ignore / Skip row</Button>
            {isFee && (
              <Button disabled={!canAccept} onClick={() => onAcceptFee(row, reason.trim())} className="bpu-cta h-9 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 text-white" data-testid="bulk-payment-review-accept"
                title={overLine ? 'Uploaded fee exceeds the outstanding line balance' : undefined}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Accept uploaded fee'}
              </Button>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

// ---- Stage 2: preview / matching ----
const PreviewStage = ({ rows, summary, busy, onIgnore, format, onReview }) => (
  <div className="px-6 pb-2 space-y-3 bpu-stage">
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="bulk-payment-summary">
      <SummaryCard label="Total Rows" count={summary.total} tone="slate" delay={0} />
      <SummaryCard label="Ready to Apply" count={summary.matched_pending_apply} tone="emerald" delay={40} />
      {format === TIER0 ? (<>
        <SummaryCard label="Fee Mismatch" count={summary.fee_mismatch || 0} tone="amber" delay={80} />
        <SummaryCard label="Product/State" count={(summary.product_mismatch || 0) + (summary.state_mismatch || 0)} tone="amber" delay={120} />
        <SummaryCard label="Not Found" count={summary.product_id_not_found || 0} tone="rose" delay={160} />
        <SummaryCard label="Dup / Paid / Review" count={(summary.duplicate || 0) + (summary.already_paid || 0) + (summary.needs_review || 0)} tone="slate" delay={200} />
      </>) : (<>
        <SummaryCard label="Partial" count={summary.partial_payment || 0} tone="blue" delay={80} />
        <SummaryCard label="Amount Mismatch" count={summary.amount_mismatch || 0} tone="amber" delay={120} />
        <SummaryCard label="Duplicates" count={summary.duplicate || 0} tone="slate" delay={160} />
        <SummaryCard label="Unmatched" count={summary.unmatched || 0} tone="rose" delay={200} />
      </>)}
    </div>
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60">
        <div className="text-sm font-semibold text-slate-900">{format === TIER0 ? 'Reconciliation preview' : 'Matching results'}</div>
        <div className="text-[11px] text-slate-500">{format === TIER0 ? 'Nothing is posted yet. Apply records payments only for Matched rows; Fee Mismatch rows need Review.' : 'Review match results. Apply will record payments only for matched rows.'}</div>
      </div>
      {format === TIER0 ? <Tier0Table rows={rows} busy={busy} onIgnore={onIgnore} onReview={onReview} /> : (
      <div className="overflow-auto max-h-[42vh]">
        <table className="w-full text-sm">
          <thead className="tf-thead text-[11px] sticky top-0 bg-white z-10">
            <tr>
              <th className="text-left px-3 py-2 font-medium">#</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Tier</th>
              <th className="text-left px-3 py-2 font-medium">Invoice</th>
              <th className="text-left px-3 py-2 font-medium">Order ID</th>
              <th className="text-right px-3 py-2 font-medium">Paid</th>
              <th className="text-right px-3 py-2 font-medium">Invoice Total</th>
              <th className="text-left px-3 py-2 font-medium">Reference</th>
              <th className="text-left px-3 py-2 font-medium">Reason / Note</th>
              <th className="text-left px-3 py-2 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const meta = r.match_meta || {};
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/40 bpu-row" data-testid={`bulk-payment-row-${r.id}`}>
                  <td className="px-3 py-1.5 text-slate-500 text-xs">{r.row_number}</td>
                  <td className="px-3 py-1.5"><StatusPill status={r.status} /></td>
                  <td className="px-3 py-1.5 text-xs text-slate-600">{r.tier ?? '—'}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{meta.invoice_number || r.invoice_number || '—'}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.product_id || '—'}</td>
                  <td className="px-3 py-1.5 text-right font-medium">{fmtUsd(r.amount_paid)}</td>
                  <td className="px-3 py-1.5 text-right text-slate-600">{fmtUsd(meta.invoice_total)}</td>
                  <td className="px-3 py-1.5 text-xs">{r.payment_reference || '—'}</td>
                  <td className="px-3 py-1.5 text-xs text-slate-500 max-w-[200px] truncate" title={r.reason || ''}>{r.reason || '—'}</td>
                  <td className="px-3 py-1.5">
                    {EXCEPTION_STATUSES.includes(r.status) && (
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => onIgnore(r)}
                        className="h-7 px-2 text-xs text-slate-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`bulk-payment-ignore-${r.id}`}>
                        Ignore
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  </div>
);

// ---- Stage 3: result ----
const ResultStage = ({ applyResult, summary }) => (
  <div className="px-6 pb-2 space-y-4 bpu-stage" data-testid="bulk-payment-result">
    <div className="flex items-center gap-3">
      <div className="bpu-check w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle2 className="w-6 h-6 text-emerald-700" />
      </div>
      <div>
        <div className="text-base font-semibold text-slate-900">Bulk apply complete</div>
        <p className="text-sm text-slate-600">
          Applied {applyResult.applied} payment{applyResult.applied === 1 ? '' : 's'};
          {applyResult.failed > 0 && <> failed {applyResult.failed};</>}
          {applyResult.skipped_idempotent > 0 && <> skipped {applyResult.skipped_idempotent} (already-applied);</>}
          {' '}exceptions remaining: {summary.exceptions}.
        </p>
      </div>
    </div>
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
      <SummaryCard label="Total Rows" count={summary.total} tone="slate" delay={0} />
      <SummaryCard label="Applied" count={summary.applied || applyResult.applied} tone="emerald" delay={40} />
      <SummaryCard label="Partial" count={summary.partial_payment || 0} tone="blue" delay={80} />
      <SummaryCard label="Amount Mismatch" count={summary.amount_mismatch || 0} tone="amber" delay={120} />
      <SummaryCard label="Duplicates" count={summary.duplicate || 0} tone="slate" delay={160} />
      <SummaryCard label="Unmatched" count={summary.unmatched || 0} tone="rose" delay={200} />
    </div>
  </div>
);

export const BulkPaymentUploadModal = ({ open, onOpenChange, onApplied }) => {
  const [stage, setStage] = useState('upload');
  const [file, setFile] = useState(null);
  const [rowCount, setRowCount] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [job, setJob] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [format, setFormat] = useState('legacy');
  const [reviewRow, setReviewRow] = useState(null);

  useEffect(() => {
    if (!open) {
      setStage('upload'); setFile(null); setRowCount(null); setDragging(false);
      setJob(null); setRows([]); setSummary(null); setBusy(false); setApplyResult(null);
      setFormat('legacy'); setReviewRow(null);
    }
  }, [open]);

  const handleFilePick = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) { toast.error('Only .csv files are accepted in V1.'); return; }
    setFile(f); setRowCount(null);
    const reader = new FileReader();
    reader.onload = () => {
      const lines = String(reader.result || '').split(/\r?\n/).filter((l) => l.trim() !== '');
      setRowCount(Math.max(lines.length - 1, 0));
    };
    reader.readAsText(f.slice(0, 5 * 1024 * 1024));
  };

  const handleUpload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/pricing/billing/payments/bulk-upload`, fd);
      setJob(r.data.job_id); setRows(r.data.rows); setSummary(r.data.summary);
      setFormat(r.data.format || 'legacy');
      setStage('preview');
      toast.success(`Parsed ${r.data.summary.total} rows.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Upload failed.');
    } finally { setBusy(false); }
  };

  const refetchPreview = useCallback(async () => {
    if (!job) return;
    const r = await axios.get(`${API}/pricing/billing/payments/bulk-upload/${job}`);
    setRows(r.data.rows); setSummary(r.data.summary);
  }, [job]);

  const handleApply = async () => {
    if (!job) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pricing/billing/payments/bulk-upload/${job}/apply`);
      setApplyResult(r.data);
      await refetchPreview();
      setStage('result');
      toast.success(`Applied ${r.data.applied} payment${r.data.applied === 1 ? '' : 's'}.`);
      onApplied?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Apply failed.');
    } finally { setBusy(false); }
  };

  const handleIgnoreRow = async (row) => {
    setBusy(true);
    try {
      await axios.post(`${API}/pricing/billing/payments/bulk-upload/${job}/rows/${row.id}/resolve`, { action: 'ignore' });
      await refetchPreview();
      setReviewRow(null);
      toast.success(`Row #${row.row_number} ignored.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Resolve failed.');
    } finally { setBusy(false); }
  };

  // BULK-PAY-PID-001 — Fee Mismatch review: accept the uploaded fee (audited; posted only on Apply)
  const handleAcceptFee = async (row, reason) => {
    setBusy(true);
    try {
      await axios.post(`${API}/pricing/billing/payments/bulk-upload/${job}/rows/${row.id}/resolve`, { action: 'accept_fee', note: reason });
      await refetchPreview();
      setReviewRow(null);
      toast.success(`Row #${row.row_number}: uploaded fee accepted — will post on Apply.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Could not accept the fee.');
    } finally { setBusy(false); }
  };

  const close = () => onOpenChange(false);
  const wide = stage !== 'upload';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="bpu-overlay fixed inset-0 z-50" data-testid="bulk-payment-backdrop" />
        <DialogPrimitive.Content
          onInteractOutside={(e) => e.preventDefault()}
          className={`bpu-content fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] ${wide ? 'max-w-4xl' : 'max-w-[680px]'} -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-[0_24px_64px_rgba(15,23,42,0.28)] focus:outline-none max-h-[90vh] flex flex-col`}
          data-testid="bulk-payment-modal"
          aria-busy={busy}
        >
          <div className="flex items-start gap-3 px-6 pt-5 pb-4">
            <span className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Upload className="w-5 h-5" /></span>
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-lg font-semibold text-slate-900 leading-tight">Bulk Payment Upload</DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-[13px] text-slate-500 mt-0.5">
                {stage === 'upload' && 'Upload a CSV of received payments and match to orders or invoices.'}
                {stage === 'preview' && `${file?.name || 'CSV'} matched — review results, then apply.`}
                {stage === 'result' && 'Payments recorded. Exceptions remain available for review.'}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <button type="button" aria-label="Close" disabled={busy}
                className="bpu-icon-btn w-9 h-9 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 flex items-center justify-center disabled:opacity-40"
                data-testid="bulk-payment-close">
                <X className="w-5 h-5" />
              </button>
            </DialogPrimitive.Close>
          </div>

          <div className="overflow-y-auto flex-1 min-h-0" key={stage}>
            {stage === 'upload' && (
              <UploadStage file={file} rowCount={rowCount} onPick={handleFilePick}
                onClear={() => { setFile(null); setRowCount(null); }} dragging={dragging} setDragging={setDragging} />
            )}
            {stage === 'preview' && summary && <PreviewStage rows={rows} summary={summary} busy={busy} onIgnore={handleIgnoreRow} format={format} onReview={setReviewRow} />}
            {stage === 'result' && applyResult && summary && <ResultStage applyResult={applyResult} summary={summary} />}
          </div>

          <div className="flex items-center justify-end gap-2 px-6 py-4">
            {stage === 'upload' && (
              <>
                <Button variant="outline" onClick={close} disabled={busy} className="h-10 px-5 rounded-lg" data-testid="bulk-payment-cancel">Cancel</Button>
                <Button onClick={handleUpload} disabled={!file || busy}
                  className={`bpu-cta h-10 px-5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white ${busy ? 'is-busy' : ''}`}
                  data-testid="bulk-payment-upload-btn">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Matching…</> : <><Sparkles className="w-4 h-4 mr-2" />Upload &amp; Match</>}
                </Button>
              </>
            )}
            {stage === 'preview' && summary && (
              <>
                <Button variant="outline" onClick={close} disabled={busy} className="h-10 px-5 rounded-lg" data-testid="bulk-payment-cancel">Cancel</Button>
                <Button onClick={handleApply} disabled={busy || summary.matched_pending_apply === 0}
                  className={`bpu-cta h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white ${busy ? 'is-busy' : ''}`}
                  data-testid="bulk-payment-apply-btn">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Applying…</> : `Apply ${summary.matched_pending_apply} Matched`}
                </Button>
              </>
            )}
            {stage === 'result' && (
              <>
                <Button variant="outline" onClick={() => setStage('preview')} className="h-10 px-5 rounded-lg" data-testid="bulk-payment-back-to-preview">Back to Preview</Button>
                <Button onClick={close} className="bpu-cta h-10 px-5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="bulk-payment-done">Done</Button>
              </>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
      {reviewRow && (
        <ReviewDialog row={rows.find((r) => r.id === reviewRow.id) || reviewRow} busy={busy}
          onClose={() => setReviewRow(null)} onIgnore={handleIgnoreRow} onAcceptFee={handleAcceptFee} />
      )}
    </DialogPrimitive.Root>
  );
};

export default BulkPaymentUploadModal;
